import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import BookingDetailsModal from './components/BookingDetailsModal';
import PaymentModal from './components/PaymentModal';
import BookingItemCard from './components/BookingItemCard';
import ReviewModal from './components/ReviewModal';
import { getBookings } from '../../services/bookingService';
import { useAuth } from '../../contexts/AuthContext';

const BookingsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [bookingToPay, setBookingToPay] = useState(null);
  const [bookingToReview, setBookingToReview] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  useEffect(() => {
    const loadBookings = async () => {
      if (user?.id) {
        const loadedBookings = await getBookings(user?.id);
        setUserBookings(loadedBookings);
      } else {
        // Fallback to localStorage if not authenticated
        const localBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
        setUserBookings(localBookings);
      }
    };
    
    loadBookings();
  }, [user?.id]);

  const mockBookings = [
  {
    id: 'BK001',
    type: 'accommodation',
    name: 'Boracay Beachfront Resort',
    location: 'Boracay, Aklan',
    description: 'Deluxe ocean view room with balcony and complimentary breakfast',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b8dff5a-1768034630554.png",
    imageAlt: 'Luxury beachfront resort room with ocean view balcony and modern amenities in Boracay Island',
    checkIn: '2026-03-15',
    checkOut: '2026-03-20',
    guests: 2,
    amount: 25000,
    paymentStatus: 'pending',
    bookingStatus: 'confirmed',
    confirmationNumber: 'CONF-2026-001',
    paymentMethod: 'pending',
    dueDate: '2026-03-10',
    cancellationPolicy: 'Free cancellation until 7 days before check-in. 50% refund for cancellations 3-7 days before. No refund for cancellations within 3 days.',
    refundPolicy: 'Full refund processed within 7-10 business days to original payment method',
    contactEmail: 'reservations@boracayresort.ph',
    contactPhone: '+63 917 123 4567'
  },
  {
    id: 'BK002',
    type: 'activity',
    name: 'El Nido Island Hopping Tour A',
    location: 'El Nido, Palawan',
    description: 'Full day tour including Big Lagoon, Small Lagoon, Secret Lagoon, and Shimizu Island',
    image: 'https://images.unsplash.com/photo-1611043718550-977bc9ae2429',
    imageAlt: 'Traditional Filipino outrigger boat navigating through crystal clear lagoon surrounded by limestone cliffs in El Nido',
    checkIn: '2026-04-10',
    checkOut: '2026-04-10',
    guests: 4,
    amount: 6000,
    paymentStatus: 'paid',
    bookingStatus: 'confirmed',
    confirmationNumber: 'CONF-2026-002',
    paymentMethod: 'gcash',
    dueDate: null,
    cancellationPolicy: 'Free cancellation until 24 hours before tour. No refund for cancellations within 24 hours.',
    refundPolicy: 'Full refund processed within 3-5 business days',
    contactEmail: 'tours@elnidoadventures.ph',
    contactPhone: '+63 918 234 5678'
  },
  {
    id: 'BK003',
    type: 'transportation',
    name: 'Manila to Cebu Round Trip Flight',
    location: 'Manila - Cebu',
    description: 'Economy class tickets with 20kg baggage allowance',
    image: "https://images.unsplash.com/photo-1558285550-f0937eec9d82",
    imageAlt: 'Commercial airplane flying above clouds during sunset with wing visible from passenger window',
    checkIn: '2026-05-01',
    checkOut: '2026-05-05',
    guests: 2,
    amount: 8500,
    paymentStatus: 'paid',
    bookingStatus: 'confirmed',
    confirmationNumber: 'CONF-2026-003',
    paymentMethod: 'credit_card',
    dueDate: null,
    cancellationPolicy: 'Non-refundable. Date changes allowed with PHP 1,500 rebooking fee per passenger.',
    refundPolicy: 'No refund available for this booking type',
    contactEmail: 'support@philippineairlines.com',
    contactPhone: '+63 2 8855 8888'
  },
  {
    id: 'BK004',
    type: 'accommodation',
    name: 'Baguio Mountain Lodge',
    location: 'Baguio City',
    description: 'Cozy cabin with fireplace and mountain view',
    image: "https://images.unsplash.com/photo-1513691548221-ba298df5fded",
    imageAlt: 'Rustic wooden mountain lodge surrounded by pine trees with misty mountain backdrop in Baguio',
    checkIn: '2026-06-15',
    checkOut: '2026-06-18',
    guests: 3,
    amount: 12000,
    paymentStatus: 'pending',
    bookingStatus: 'confirmed',
    confirmationNumber: 'CONF-2026-004',
    paymentMethod: 'pay_at_hotel',
    dueDate: '2026-06-15',
    cancellationPolicy: 'Free cancellation until 5 days before check-in. 30% refund for cancellations 2-5 days before.',
    refundPolicy: 'Refund processed within 5-7 business days',
    contactEmail: 'info@baguiolodge.ph',
    contactPhone: '+63 919 345 6789'
  },
  {
    id: 'BK005',
    type: 'activity',
    name: 'Siargao Surfing Lessons',
    location: 'Siargao Island',
    description: '3-day beginner surfing course with equipment included',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_185718670-1765205413169.png",
    imageAlt: 'Surfer riding perfect wave with surfboard on tropical beach with palm trees in Siargao Island',
    checkIn: '2026-01-20',
    checkOut: '2026-01-22',
    guests: 1,
    amount: 4500,
    paymentStatus: 'paid',
    bookingStatus: 'completed',
    confirmationNumber: 'CONF-2026-005',
    paymentMethod: 'gcash',
    dueDate: null,
    cancellationPolicy: 'No cancellation allowed for completed bookings',
    refundPolicy: 'No refund available',
    contactEmail: 'lessons@siargaosurf.ph',
    contactPhone: '+63 920 456 7890'
  },
  {
    id: 'BK006',
    type: 'accommodation',
    name: 'Cebu Heritage Hotel',
    location: 'Cebu City',
    description: 'Historic hotel in downtown Cebu near major attractions',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ece3cf60-1769691418296.png",
    imageAlt: 'Colonial era heritage hotel building with Spanish architecture and modern interior in Cebu City',
    checkIn: '2026-01-10',
    checkOut: '2026-01-14',
    guests: 2,
    amount: 18000,
    paymentStatus: 'paid',
    bookingStatus: 'completed',
    confirmationNumber: 'CONF-2026-006',
    paymentMethod: 'pay_at_hotel',
    dueDate: null,
    cancellationPolicy: 'No cancellation allowed for completed bookings',
    refundPolicy: 'No refund available',
    contactEmail: 'reservations@cebuheritage.ph',
    contactPhone: '+63 32 123 4567'
  },
  {
    id: 'BK007',
    type: 'accommodation',
    name: 'Palawan Beach Villa',
    location: 'Puerto Princesa, Palawan',
    description: 'Private beach villa with infinity pool and butler service',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_173fac3c8-1770048521168.png",
    imageAlt: 'Luxury private beach villa with infinity pool overlooking turquoise ocean and white sand beach in Palawan',
    checkIn: '2026-02-20',
    checkOut: '2026-02-22',
    guests: 2,
    amount: 35000,
    paymentStatus: 'failed',
    bookingStatus: 'pending',
    confirmationNumber: 'CONF-2026-007',
    paymentMethod: 'pending',
    dueDate: '2026-02-15',
    cancellationPolicy: 'Free cancellation until 14 days before check-in. 25% refund for cancellations 7-14 days before.',
    refundPolicy: 'Refund processed within 10-14 business days',
    contactEmail: 'luxury@palawanvilla.ph',
    contactPhone: '+63 921 567 8901'
  }];


  const allBookings = [...mockBookings, ...userBookings];

  const getFilteredBookings = () => {
    let filtered = allBookings;

    // Filter by tab
    if (activeTab === 'active') {
      filtered = filtered?.filter((b) => b?.bookingStatus === 'confirmed' || b?.bookingStatus === 'pending');
    } else if (activeTab === 'history') {
      filtered = filtered?.filter((b) => b?.bookingStatus === 'completed' || b?.bookingStatus === 'cancelled');
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered?.filter((b) => b?.paymentStatus === filterStatus);
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter((b) =>
      b?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      b?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      b?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Sort
    if (sortBy === 'date') {
      filtered = filtered?.sort((a, b) => new Date(b?.checkIn) - new Date(a?.checkIn));
    } else if (sortBy === 'amount') {
      filtered = filtered?.sort((a, b) => b?.amount - a?.amount);
    } else if (sortBy === 'status') {
      filtered = filtered?.sort((a, b) => a?.paymentStatus?.localeCompare(b?.paymentStatus));
    }

    return filtered;
  };

  const handleViewDetails = (bookingId) => {
    const booking = allBookings?.find((b) => b?.id === bookingId);
    setSelectedBooking(booking);
    setIsDetailsModalOpen(true);
  };

  const handlePayNow = (bookingId) => {
    const booking = allBookings?.find((b) => b?.id === bookingId);
    setBookingToPay(booking);
    setIsPaymentModalOpen(true);
  };

  const handleWriteReview = (booking) => {
    setBookingToReview(booking);
    setIsReviewModalOpen(true);
  };

  const handleReviewSubmitted = () => {
    setReviewSubmitted(true);
    setTimeout(() => setReviewSubmitted(false), 3000);
  };

  const handleCancelBooking = (bookingId) => {
    if (window?.confirm('Are you sure you want to cancel this booking? Cancellation policies will apply.')) {
      alert(`Booking ${bookingId} cancellation requested. You will receive a confirmation email shortly.`);
    }
  };

  const handlePaymentSuccess = (bookingId, paymentMethod) => {
    alert(`Payment successful! Confirmation sent to your email.`);
    setIsPaymentModalOpen(false);
    setBookingToPay(null);
  };

  const filteredBookings = getFilteredBookings();

  const sortedBookings = [...filteredBookings]?.sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b?.checkIn) - new Date(a?.checkIn);
    } else if (sortBy === 'amount') {
      return b?.amount - a?.amount;
    } else if (sortBy === 'status') {
      return a?.paymentStatus?.localeCompare(b?.paymentStatus);
    }
  });

  const activeBookings = sortedBookings?.filter(b => 
    b?.bookingStatus === 'confirmed' || b?.bookingStatus === 'pending'
  );
  const completedBookings = sortedBookings?.filter(b => b?.bookingStatus === 'completed');
  const cancelledBookings = sortedBookings?.filter(b => b?.bookingStatus === 'cancelled');

  const displayBookings = activeTab === 'active' ? activeBookings : 
                         activeTab === 'completed' ? completedBookings : 
                         cancelledBookings;

  const tabs = [
  { id: 'active', label: 'Active Bookings', icon: 'Calendar', count: [...mockBookings, ...userBookings]?.filter((b) => b?.bookingStatus === 'confirmed' || b?.bookingStatus === 'pending')?.length },
  { id: 'history', label: 'Booking History', icon: 'Clock', count: mockBookings?.filter((b) => b?.bookingStatus === 'completed')?.length },
  { id: 'payments', label: 'Payment Methods', icon: 'CreditCard', count: null }];


  const sortOptions = [
  { value: 'date', label: 'Sort by Date' },
  { value: 'amount', label: 'Sort by Amount' },
  { value: 'status', label: 'Sort by Status' }];


  const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' }];


  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-20 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">My Bookings</h1>
            <p className="text-muted-foreground">Manage your reservations, payments, and booking history</p>
          </div>

          {/* Tab Navigation */}
          <div className="bg-card border-b border-border mb-6 rounded-t-xl overflow-hidden">
            <div className="flex gap-1 p-2">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-250 ${
                activeTab === tab?.id ?
                'bg-primary text-primary-foreground shadow-md' :
                'text-muted-foreground hover:text-foreground hover:bg-muted'}`
                }>

                  <Icon name={tab?.icon} size={18} />
                  <span className="text-sm">{tab?.label}</span>
                  {tab?.count !== null &&
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                activeTab === tab?.id ?
                'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`
                }>
                      {tab?.count}
                    </span>
                }
                </button>
              )}
            </div>
          </div>

          {/* Active Bookings & History Tabs */}
          {(activeTab === 'active' || activeTab === 'history') &&
          <>
              {/* Filters & Search */}
              <div className="bg-card rounded-xl border border-border p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="relative">
                    <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                    type="text"
                    placeholder="Search bookings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10" />

                  </div>
                  <Select
                  options={sortOptions}
                  value={sortBy}
                  onChange={setSortBy}
                  placeholder="Sort by" />

                  <Select
                  options={statusOptions}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  placeholder="Filter by status" />

                </div>
              </div>

              {/* Bookings List */}
              <div className="space-y-4">
                {displayBookings?.length === 0 ?
              <div className="bg-card rounded-xl border border-border p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                      <Icon name="Calendar" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">No bookings found</h3>
                    <p className="text-muted-foreground mb-6">Try adjusting your search or filters</p>
                    <Button
                  variant="default"
                  onClick={() => navigate('/homepage')}
                  iconName="Search"
                  iconPosition="left">

                      Explore Destinations
                    </Button>
                  </div> :

              displayBookings?.map((booking) =>
              <BookingItemCard
                key={booking?.id}
                booking={booking}
                onViewDetails={handleViewDetails}
                onPayNow={handlePayNow}
                onCancel={handleCancelBooking}
                onWriteReview={handleWriteReview} />

              )
              }
              </div>
            </>
          }

          {/* Payment Methods Tab */}
          {activeTab === 'payments' &&
          <div className="space-y-6">
              {/* Payment Summary */}
              <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-6 text-white">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-white/80 text-sm mb-1">Outstanding Balance</p>
                    <h2 className="text-3xl font-bold">₱37,000.00</h2>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <Icon name="Wallet" size={24} />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="AlertCircle" size={16} />
                  <span>2 payments due within 7 days</span>
                </div>
              </div>

              {/* Payment Options */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="p-6 border-b border-border">
                  <h3 className="text-xl font-bold text-foreground">Available Payment Methods</h3>
                  <p className="text-sm text-muted-foreground mt-1">Choose your preferred payment option</p>
                </div>

                <div className="p-6 space-y-4">
                  {/* GCash */}
                  <div className="flex items-center justify-between p-4 rounded-lg border-2 border-primary bg-primary/5">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Icon name="Smartphone" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">GCash</h4>
                        <p className="text-sm text-muted-foreground">Instant payment via GCash wallet</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full bg-success text-success-foreground text-xs font-semibold">Recommended</span>
                      <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                    </div>
                  </div>

                  {/* Credit/Debit Card */}
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all cursor-pointer">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                        <Icon name="CreditCard" size={24} className="text-foreground" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Credit/Debit Card</h4>
                        <p className="text-sm text-muted-foreground">Visa, Mastercard, JCB accepted</p>
                      </div>
                    </div>
                    <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                  </div>

                  {/* Pay at Hotel */}
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all cursor-pointer">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                        <Icon name="Building" size={24} className="text-foreground" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Pay at Hotel</h4>
                        <p className="text-sm text-muted-foreground">Pay when you arrive at the property</p>
                      </div>
                    </div>
                    <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                  </div>

                  {/* Bank Transfer */}
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all cursor-pointer">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                        <Icon name="Landmark" size={24} className="text-foreground" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">Bank Transfer</h4>
                        <p className="text-sm text-muted-foreground">Direct bank transfer (1-2 days processing)</p>
                      </div>
                    </div>
                    <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                  </div>
                </div>
              </div>

              {/* Payment Security */}
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Shield" size={20} className="text-success" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Secure Payment Processing</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      All transactions are encrypted and secured with industry-standard SSL technology. Your payment information is never stored on our servers.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 rounded-full bg-muted text-xs font-medium">PCI DSS Compliant</span>
                      <span className="px-3 py-1 rounded-full bg-muted text-xs font-medium">256-bit Encryption</span>
                      <span className="px-3 py-1 rounded-full bg-muted text-xs font-medium">Fraud Protection</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </main>

      {/* Modals */}
      <BookingDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        booking={selectedBooking}
        onPayNow={handlePayNow}
        onCancel={handleCancelBooking}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        booking={bookingToPay}
        onPaymentComplete={handlePaymentSuccess}
      />

      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        booking={bookingToReview}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </div>);

};

export default BookingsDashboard;